import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'area[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Makes a subtree inert while `active` is true.
 * - Prefers the native `inert` attribute, falls back to wiping tab stops.
 * - Adds `aria-hidden` during inerting.
 * - Remembers and restores the previously focused element inside the subtree.
 *
 * @param {React.RefObject<HTMLElement>} targetRef - Container to toggle inertness on.
 * @param {boolean} active - When true, inert/disable tab stops; when false, restore.
 */
export default function useInert(targetRef, active) {
  const previousFocusRef = useRef(null);
  const tabStateRef = useRef([]);

  useEffect(() => {
    const target = targetRef?.current;
    if (!target) return undefined;

    const supportsInert = 'inert' in target;

    if (!active) {
      // Ensure any lingering state is cleaned up if the component remounts.
      if (supportsInert) {
        target.inert = false;
        target.removeAttribute('inert');
      } else if (tabStateRef.current.length) {
        tabStateRef.current.forEach(({ node, tabIndex }) => {
          if (!node || !node.isConnected) return;
          if (tabIndex === null) node.removeAttribute('tabindex');
          else node.setAttribute('tabindex', tabIndex);
        });
        tabStateRef.current = [];
      }
      target.removeAttribute('aria-hidden');
      previousFocusRef.current = null;
      return undefined;
    }

    if (target.contains(document.activeElement)) {
      previousFocusRef.current = document.activeElement;
    }

    target.setAttribute('aria-hidden', 'true');

    if (supportsInert) {
      target.inert = true;
      target.setAttribute('inert', '');
    } else {
      const focusable = Array.from(target.querySelectorAll(FOCUSABLE_SELECTORS));
      tabStateRef.current = focusable.map((node) => ({ node, tabIndex: node.getAttribute('tabindex') }));
      focusable.forEach((node) => node.setAttribute('tabindex', '-1'));
    }

    return () => {
      if (supportsInert) {
        target.inert = false;
        target.removeAttribute('inert');
      } else if (tabStateRef.current.length) {
        tabStateRef.current.forEach(({ node, tabIndex }) => {
          if (!node || !node.isConnected) return;
          if (tabIndex === null) node.removeAttribute('tabindex');
          else node.setAttribute('tabindex', tabIndex);
        });
        tabStateRef.current = [];
      }
      target.removeAttribute('aria-hidden');

      if (previousFocusRef.current && target.contains(previousFocusRef.current)) {
        try {
          previousFocusRef.current.focus({ preventScroll: true });
        } catch (error) {
          // ignore
        }
      }
      previousFocusRef.current = null;
    };
  }, [targetRef, active]);
}
